// ---------------------------------------------------------------------------
// HRIS · Staff attendance QR helpers
// ---------------------------------------------------------------------------
// Pure helpers shared by the QR generator page (admin) and the public check-in
// page (staff mobile): reading school context out of localStorage userInfo,
// building / parsing the check-in URL, and minting session tokens.
// ---------------------------------------------------------------------------
import {STAFF_ATTENDANCE_CHECKIN_ROUTE} from "../constants/HrisConstants";

// Pulls school identity from the logged-in user stored at login time.
// userInfo uses `clientName` for the school name in this app, so we read that
// first and fall back through the other likely keys defensively.
export function getSchoolContext() {
    let userInfo = {};
    try {
        userInfo = JSON.parse(localStorage.getItem("userInfo")) || {};
    } catch (e) {
        userInfo = {};
    }
    const schoolName =
        userInfo.schoolName || userInfo.clientName || userInfo.name || "";
    const schoolCode =
        userInfo.schoolCode || userInfo.clientCode || userInfo.code || "";
    const schoolId =
        userInfo.schoolId ?? userInfo.clientId ?? userInfo.id ?? null;
    return {schoolName, schoolCode, schoolId};
}

// Today's date as yyyy-mm-dd (local).
export function todayISO() {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const pad2 = (n) => String(n).padStart(2, "0");

// Computes a timezone's UTC offset (e.g. "+05:45") for a given instant, derived
// from Intl so it stays correct without hardcoding.
function tzOffset(date, timeZone) {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        hour12: false,
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
    }).formatToParts(date).reduce((acc, p) => {
        acc[p.type] = p.value;
        return acc;
    }, {});
    const hour = parts.hour === "24" ? 0 : Number(parts.hour);
    const asUTC = Date.UTC(
        Number(parts.year), Number(parts.month) - 1, Number(parts.day),
        hour, Number(parts.minute), Number(parts.second)
    );
    const diffMin = Math.round((asUTC - date.getTime()) / 60000);
    const sign = diffMin >= 0 ? "+" : "-";
    const abs = Math.abs(diffMin);
    return `${sign}${pad2(Math.floor(abs / 60))}:${pad2(abs % 60)}`;
}

// ISO-8601 timestamp for an instant expressed in Asia/Kathmandu (Nepal, +05:45),
// e.g. "2026-08-15T15:45:00+05:45". Used for staff check-in / check-out so the
// server receives the punch time in Nepal time regardless of the device's clock.
export function kathmanduISOString(date = new Date()) {
    const timeZone = "Asia/Kathmandu";
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        hour12: false,
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
    }).formatToParts(date).reduce((acc, p) => {
        acc[p.type] = p.value;
        return acc;
    }, {});
    const hour = parts.hour === "24" ? "00" : parts.hour;
    return `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}:${parts.second}${tzOffset(date, timeZone)}`;
}

// Short, hard-to-guess session token so each generated QR is unique and can be
// tied to a specific generation moment.
export function makeSessionToken() {
    const rnd = Math.random().toString(36).slice(2, 8);
    return `${Date.now().toString(36)}${rnd}`;
}

// Builds the absolute check-in URL the QR encodes. Uses the app's hash router,
// so it works wherever the SPA is hosted (respects PUBLIC_URL).
export function buildCheckinUrl({schoolCode, schoolName, schoolId, session, date}) {
    const origin = window.location.origin;
    const base = process.env.PUBLIC_URL || "";
    const params = new URLSearchParams();
    if (schoolCode) params.set("sc", schoolCode);
    if (schoolName) params.set("sn", schoolName);
    if (schoolId != null && schoolId !== "") params.set("sid", String(schoolId));
    if (session) params.set("session", session);
    if (date) params.set("d", date);
    return `${origin}${base}/#${STAFF_ATTENDANCE_CHECKIN_ROUTE}?${params.toString()}`;
}

// Reads the check-in context back out of a location.search string on the
// public page. Returns {} for the keys that are absent.
export function parseCheckinParams(search) {
    const p = new URLSearchParams(search || "");
    return {
        schoolCode: p.get("sc") || "",
        schoolName: p.get("sn") || "",
        schoolId: p.get("sid") || "",
        session: p.get("session") || "",
        date: p.get("d") || "",
    };
}
